import COMMON from 'constants/common';
import React, { useEffect, useState } from 'react';
import Layout from 'components/system/layouts/Layout';
import ScreenContext from 'Contexts/Common/ScreenContext';
import MachineConfContent from 'components/machineConf/organisms/contents/MachineConfContent';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ReturnButton, SelectButton } from 'components/system/atoms';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getClassificationName } from 'utils/common.helper';
import CLSFCN from 'constants/classification';
import redirectLinks from 'constants/redirectLinks';
import { useNavigate, useLocation } from 'react-router-dom';
import { handleCallSystemError } from 'redux/slices/commonSlice';
import ERROR_CODE from 'constants/errorCode';
import UrlHelper from 'utils/url.helper';
import multiMachine from 'assets/images/multiMachine.svg';
import { SaveDetailInfoType } from 'types/machineConf/stationSelectType';

/* ************ Context ************ */
const ScreenContextValue = {
  title: '照明演出設定',
  screenId: COMMON.SCREEN_ID.LED_CONF_MULTI,
  currentMenu: COMMON.MENU.MACHINE_CONF,
  disableContents: true,
};

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '1080px',
    minHeight: '563px',
    position: 'relative',
    margin: '18px 24px',
  },
  topContent: {
    display: 'flex',
    '& .returnButton': {
      fontSize: '20px',
      lineHeight: '24px',
      position: 'relative',
      zIndex: 200,
    },
    '& .description': {
      fontSize: '23px',
      marginLeft: '16px',
    },
  },
  middleContent: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    marginTop: '24px',
    '& .ledSelectArea': { display: 'flex', justifyContent: 'center', alignItems: 'flex-end', width: '100%' },
    '&& .settingButton': {
      width: '224px',
      height: '48px',
      fontSize: '19px',
      border: '2px solid #000',
      borderRadius: '8px',
      padding: '0 8px',
      marginTop: '5px',
      '& div': {
        textAlign: 'left',
      },
    },
    '& .ledSetting': {
      width: '256px',
    },
    '& .ledDirecting, .ecoMode, .keepEcoType': {
      marginTop: '16px',
      width: '256px',
    },
  },
  // Common Style
  baseFontSize: {
    fontSize: '21px',
    lineHeight: '24px',
  },
  multiImage: {
    width: '159px',
    height: '295px',
    '& .multiMachine': {
      display: 'inline',
    },
  },
  onOffValue: { fontSize: '21px', lineHeight: '24px' },
}));

/* ******** Constants ******** */

/* ******** Main Component ******** */
/**
 *
 * @returns {React.FC} - 照明演出設定
 *
 */
const LedConfMulti: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [savedConfProps] = useState(
    location.state !== undefined && location.state !== null ? (location.state as SaveDetailInfoType) : null,
  );
  const dispatch = useAppDispatch();

  /* ************ State ************ */
  // Global
  const confMulti = useAppSelector((state) => state.machineConf.machineMultiConf.confMulti);
  const { selectedStationList } = useAppSelector((state) => state.machineConf);
  const { confId: selfConfId } = selectedStationList[0];

  /* ************ Event ************ */
  // 画面表示時
  useEffect(() => {
    // グローバルステートが見つからない場合、システムエラー画面に遷移
    if (!confMulti) {
      dispatch(handleCallSystemError({ errorNo: ERROR_CODE.NOT_FOUND }));
    }
  }, []);

  // 戻るボタン
  const handleClickReturn = () => {
    navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_MULTI_HOME_DETAIL, {
      selectConfId: savedConfProps ? 'none' : selfConfId.toString(),
    }));
  };

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <MachineConfContent savedConfProps={savedConfProps}>
          <Box className={classes.root}>
            <Box className={classes.topContent}>
              <ReturnButton className="returnButton" onClick={handleClickReturn}>
                機器設定
                <br /> 詳細
              </ReturnButton>
              <Box className="description">「照明の演出」の設定内容です。</Box>
            </Box>
            <Box className={classes.middleContent}>
              <Box className="ledSelectArea">
                <Box className={classes.multiImage}>
                  <img src={multiMachine} alt="multiMachine" className="multiMachine" />
                </Box>
              </Box>
              <Box>
                <Box className="ledSetting">
                  <Box className={classes.baseFontSize}>設定内容</Box>
                  <SelectButton className="settingButton" disabled arrowSize="0" isSelected={false}>
                    {/* クレナマルチは「カスタム設定」を持たないため、「かんたん設定」固定で表示する。 */}
                    {getClassificationName(CLSFCN.LED_SETTING.CATEGORY_CD, CLSFCN.LED_SETTING.SIMPLE)}
                  </SelectButton>
                </Box>
                <Box className="ledDirecting">
                  <Box className={classes.baseFontSize}>演出内容</Box>
                  <SelectButton className="settingButton" disabled arrowSize="0" isSelected={false}>
                    {confMulti?.ledDirecting &&
                      getClassificationName(CLSFCN.DIRECTING_CONTENT.CATEGORY_CD, confMulti.ledDirecting)}
                  </SelectButton>
                </Box>
                <Box className="ecoMode">
                  <Box className={classes.baseFontSize}>エコモード</Box>
                  <SelectButton className="settingButton" disabled arrowSize="0" isSelected={false}>
                    {confMulti?.ecoType && getClassificationName(CLSFCN.LED_ECO_TYPE.CATEGORY_CD, confMulti.ecoType)}
                  </SelectButton>
                </Box>
                <Box className="keepEcoType">
                  <Box className={classes.baseFontSize}>プレイ時もエコモード継続</Box>
                  <Box className={classes.onOffValue}>
                    {confMulti?.keepEcoType &&
                      getClassificationName(CLSFCN.LED_ECO_MODE.CATEGORY_CD, confMulti.keepEcoType)}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </MachineConfContent>
      </Layout>
    </ScreenContext.Provider>
  );
};

export default LedConfMulti;
